export function generateMomentJSFormatString(fText: string, fTolerant: boolean = true): string {
    if (fText.length === 0) {
        return '';
    }
    return fText.length === 1
        ? generateMomentJSFormatStringFromStandardFormat(fText, fTolerant)
        : generateMomentJSFormatStringFromUserFormat(fText, fTolerant);
}

enum State {
    None = 1,
    LowerD1,
    LowerD2,
    LowerD3,
    LowerD4,
    LowerF1,
    LowerF2,
    LowerF3,
    LowerF4,
    LowerF5,
    LowerF6,
    LowerF7,
    CapitalF1,
    CapitalF2,
    CapitalF3,
    CapitalF4,
    CapitalF5,
    CapitalF6,
    CapitalF7,
    LowerG,
    LowerH1,
    LowerH2,
    CapitalH1,
    CapitalH2,
    CapitalK,
    LowerM1,
    LowerM2,
    CapitalM1,
    CapitalM2,
    CapitalM3,
    CapitalM4,
    LowerS1,
    LowerS2,
    LowerT1,
    LowerT2,
    LowerY1,
    LowerY2,
    LowerY3,
    LowerY4,
    LowerY5,
    LowerZ1,
    LowerZ2,
    LowerZ3,
    InSingleQuoteLiteral,
    InDoubleQuoteLiteral,
    EscapeSequence
}

function generateMomentJSFormatStringFromUserFormat(fText: string, fTolerant: boolean): string {
    let lResult = '';

    let lState: State = State.None;
    let lTokenBuffer = '';

    const changeState = (fNewState: State) => {
        switch (lState) {
            case State.LowerD1:
                lResult += 'D';
                break;
            case State.LowerD2:
                lResult += 'DD';
                break;
            case State.LowerD3:
                lResult += 'ddd';
                break;
            case State.LowerD4:
                lResult += 'dddd';
                break;
            case State.LowerF1:
            case State.CapitalF1:
                lResult += 'S';
                break;
            case State.LowerF2:
            case State.CapitalF2:
                lResult += 'SS';
                break;
            case State.LowerF3:
            case State.CapitalF3:
                lResult += 'SSS';
                break;
            case State.LowerF4:
            case State.CapitalF4:
                lResult += 'SSSS';
                break;
            case State.LowerF5:
            case State.CapitalF5:
                lResult += 'SSSSS';
                break;
            case State.LowerF6:
            case State.CapitalF6:
                lResult += 'SSSSSS';
                break;
            case State.LowerF7:
            case State.CapitalF7:
                lResult += 'SSSSSSS';
                break;
            case State.LowerG:
                throw new Error('Era not supported in MomentJS');
            case State.LowerH1:
                lResult += 'h';
                break;
            case State.LowerH2:
                lResult += 'hh';
                break;
            case State.CapitalH1:
                lResult += 'H';
                break;
            case State.CapitalH2:
                lResult += 'HH';
                break;
            case State.LowerM1:
                lResult += 'm';
                break;
            case State.LowerM2:
                lResult += 'mm';
                break;
            case State.CapitalM1:
                lResult += 'M';
                break;
            case State.CapitalM2:
                lResult += 'MM';
                break;
            case State.CapitalM3:
                lResult += 'MMM';
                break;
            case State.CapitalM4:
                lResult += 'MMMM';
                break;
            case State.LowerS1:
                lResult += 's';
                break;
            case State.LowerS2:
                lResult += 'ss';
                break;
            case State.LowerT1:
                if (fTolerant) {
                    lResult += 'A';
                } else {
                    throw new Error('Single Letter AM/PM not supported in MomentJS');
                }
                break;
            case State.LowerT2:
                lResult += 'A';
                break;
            case State.LowerY1:
                if (fTolerant) {
                    lResult += 'YY';
                } else {
                    throw new Error('Single Letter Year not supported in MomentJS');
                }
                break;
            case State.LowerY2:
                lResult += 'YY';
                break;
            case State.LowerY3:
                if (fTolerant) {
                    lResult += 'YYYY';
                } else {
                    throw new Error('Three Letter Year not supported in MomentJS');
                }
                break;
            case State.LowerY4:
                lResult += 'YYYY';
                break;
            case State.LowerY5:
                if (fTolerant) {
                    lResult += 'Y';
                } else {
                    throw new Error('Five or more Letter Year not supported in MomentJS');
                }
                break;
            case State.LowerZ1:
            case State.LowerZ2:
                if (fTolerant) {
                    lResult += 'ZZ';
                } else {
                    throw new Error('Hours offset not supported in MomentJS');
                }
                break;
            case State.LowerZ3:
                lResult += 'Z';
                break;
            case State.InSingleQuoteLiteral:
            case State.InDoubleQuoteLiteral:
            case State.EscapeSequence:
                for (const lCharacter of lTokenBuffer) {
                    lResult += '[' + lCharacter + ']';
                }
                break;
            default:
                break;
        }

        lTokenBuffer = '';
        lState = fNewState;
    }; // End changeState
    for (const character of fText) {
        if ((lState as State) === State.EscapeSequence) {
            lTokenBuffer += character;
            changeState(State.None);
        } else if ((lState as State) === State.InDoubleQuoteLiteral) {
            if (character === '"') {
                changeState(State.None);
            } else {
                lTokenBuffer += character;
            }
        } else if ((lState as State) === State.InSingleQuoteLiteral) {
            if (character === '\'') {
                changeState(State.None);
            } else {
                lTokenBuffer += character;
            }
        } else {
            switch (character) {
                case 'd':
                    switch (lState as State) {
                        case State.LowerD1:
                            lState = State.LowerD2;
                            break;
                        case State.LowerD2:
                            lState = State.LowerD3;
                            break;
                        case State.LowerD3:
                            lState = State.LowerD4;
                            break;
                        case State.LowerD4:
                            break;
                        default:
                            changeState(State.LowerD1);
                            break;
                    }
                    break;
                case 'f':
                    switch (lState as State) {
                        case State.LowerF1:
                            lState = State.LowerF2;
                            break;
                        case State.LowerF2:
                            lState = State.LowerF3;
                            break;
                        case State.LowerF3:
                            lState = State.LowerF4;
                            break;
                        case State.LowerF4:
                            lState = State.LowerF5;
                            break;
                        case State.LowerF5:
                            lState = State.LowerF6;
                            break;
                        case State.LowerF6:
                            lState = State.LowerF7;
                            break;
                        case State.LowerF7:
                            break;
                        default:
                            changeState(State.LowerF1);
                            break;
                    }
                    break;
                case 'F':
                    switch (lState as State) {
                        case State.CapitalF1:
                            lState = State.CapitalF2;
                            break;
                        case State.CapitalF2:
                            lState = State.CapitalF3;
                            break;
                        case State.CapitalF3:
                            lState = State.CapitalF4;
                            break;
                        case State.CapitalF4:
                            lState = State.CapitalF5;
                            break;
                        case State.CapitalF5:
                            lState = State.CapitalF6;
                            break;
                        case State.CapitalF6:
                            lState = State.CapitalF7;
                            break;
                        case State.CapitalF7:
                            break;
                        default:
                            changeState(State.CapitalF1);
                            break;
                    }
                    break;
                case 'g':
                    switch (lState as State) {
                        case State.LowerG:
                            break;
                        default:
                            changeState(State.LowerG);
                            break;
                    }
                    break;
                case 'h':
                    switch (lState as State) {
                        case State.LowerH1:
                            lState = State.LowerH2;
                            break;
                        case State.LowerH2:
                            break;
                        default:
                            changeState(State.LowerH1);
                            break;
                    }
                    break;
                case 'H':
                    switch (lState as State) {
                        case State.CapitalH1:
                            lState = State.CapitalH2;
                            break;
                        case State.CapitalH2:
                            break;
                        default:
                            changeState(State.CapitalH1);
                            break;
                    }
                    break;
                case 'K':
                    changeState(State.None);
                    if (fTolerant) {
                        lResult += 'Z';
                    } else {
                        throw new Error('TimeZoneInformation not supported in MomentJS');
                    }
                    break;
                case 'm':
                    switch (lState as State) {
                        case State.LowerM1:
                            lState = State.LowerM2;
                            break;
                        case State.LowerM2:
                            break;
                        default:
                            changeState(State.LowerM1);
                            break;
                    }
                    break;
                case 'M':
                    switch (lState as State) {
                        case State.CapitalM1:
                            lState = State.CapitalM2;
                            break;
                        case State.CapitalM2:
                            lState = State.CapitalM3;
                            break;
                        case State.CapitalM3:
                            lState = State.CapitalM4;
                            break;
                        case State.CapitalM4:
                            break;
                        default:
                            changeState(State.CapitalM1);
                            break;
                    }
                    break;
                case 's':
                    switch (lState as State) {
                        case State.LowerS1:
                            lState = State.LowerS2;
                            break;
                        case State.LowerS2:
                            break;
                        default:
                            changeState(State.LowerS1);
                            break;
                    }
                    break;
                case 't':
                    switch (lState as State) {
                        case State.LowerT1:
                            lState = State.LowerT2;
                            break;
                        case State.LowerT2:
                            break;
                        default:
                            changeState(State.LowerT1);
                            break;
                    }
                    break;
                case 'y':
                    switch (lState as State) {
                        case State.LowerY1:
                            lState = State.LowerY2;
                            break;
                        case State.LowerY2:
                            lState = State.LowerY3;
                            break;
                        case State.LowerY3:
                            lState = State.LowerY4;
                            break;
                        case State.LowerY4:
                            lState = State.LowerY5;
                            break;
                        case State.LowerY5:
                            break;
                        default:
                            changeState(State.LowerY1);
                            break;
                    }
                    break;
                case 'z':
                    switch (lState as State) {
                        case State.LowerZ1:
                            lState = State.LowerZ2;
                            break;
                        case State.LowerZ2:
                            lState = State.LowerZ3;
                            break;
                        case State.LowerZ3:
                            break;
                        default:
                            changeState(State.LowerZ1);
                            break;
                    }
                    break;
                case ':':
                    changeState(State.None);
                    lResult += '[:]';
                    break;
                case '/':
                    changeState(State.None);
                    lResult += '[/]';
                    break;
                case '"':
                    changeState(State.InDoubleQuoteLiteral);
                    break;
                case '\'':
                    changeState(State.InSingleQuoteLiteral);
                    break;
                case '%':
                    changeState(State.None);
                    break;
                case '\\':
                    changeState(State.EscapeSequence);
                    break;
                default:
                    changeState(State.None);
                    lResult += '[' + character + ']';
                    break;
            }
        }
    }

    if ((lState as State) === State.EscapeSequence || (lState as State) === State.InDoubleQuoteLiteral || (lState as State) === State.InSingleQuoteLiteral) {
        throw new Error('Invalid Format String');
    }

    changeState(State.None);

    return lResult;
}

function generateMomentJSFormatStringFromStandardFormat(fText: string, fTolerant: boolean): string {
    let result: string;

    switch (fText) {
        case 'd':
            result = generateMomentJSFormatStringFromUserFormat('MM/dd/yyyy', fTolerant);
            break;
        case 'D':
            result = generateMomentJSFormatStringFromUserFormat('dddd, dd MMMM yyyy', fTolerant);
            break;
        case 'f':
            result = generateMomentJSFormatStringFromUserFormat('dddd, dd MMMM yyyy HH:mm', fTolerant);
            break;
        case 'F':
            result = generateMomentJSFormatStringFromUserFormat('dddd, dd MMMM yyyy HH:mm:ss', fTolerant);
            break;
        case 'g':
            result = generateMomentJSFormatStringFromUserFormat('MM/dd/yyyy HH:mm', fTolerant);
            break;
        case 'G':
            result = generateMomentJSFormatStringFromUserFormat('MM/dd/yyyy HH:mm:ss', fTolerant);
            break;
        case 'M':
        case 'm':
            result = generateMomentJSFormatStringFromUserFormat('MMMM dd', fTolerant);
            break;
        case 'O':
        case 'o':
            result = 'YYYY-MM-DDTHH:mm:ss[Z]';
            // No idea why logic app is not following the real standard
            // result = generateMomentJSFormatStringFromUserFormat("yyyy'-'MM'-'dd'T'HH':'mm':'ss'.'fffffffK", fTolerant);
            break;
        case 'R':
        case 'r':
            throw new Error('RFC 1123 not supported  in MomentJS');
        case 's':
            result = generateMomentJSFormatStringFromUserFormat('yyyy\'-\'MM\'-\'dd\'T\'HH\':\'mm\':\'ss', fTolerant);
            break;
        case 't':
            result = generateMomentJSFormatStringFromUserFormat('HH:mm', fTolerant);
            break;
        case 'T':
            result = generateMomentJSFormatStringFromUserFormat('HH:mm:ss', fTolerant);
            break;
        case 'u':
            throw new Error('Universal Sortable Format not supported in MomentJS');
        case 'U':
            throw new Error('Universal Fulll Format not supported in MomentJS');
        case 'Y':
        case 'y':
            result = generateMomentJSFormatStringFromUserFormat('yyyy MMMM', fTolerant);
            break;
        default:
            throw new Error('Unknown Standard DateTime Format');
    }

    return result;
}
